import { ArrowUpRight, ExternalLink, Play, MousePointerClick } from "lucide-react";

const IMG = "/assets/fin-reco-demo.png";
const URL = "https://fin-reco.demo.intugle.ai/";

const Chrome = () => (
  <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
    <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
    <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">fin-reco.demo.intugle.ai</span>
  </div>
);

/* A · Centered launch button (video/app-store style) */
const OptA = () => (
  <a href={URL} target="_blank" rel="noopener noreferrer" className="group block relative rounded-xl overflow-hidden border border-[#3E2818]/12 bg-white shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300">
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
      <div className="absolute inset-0 bg-[#211307]/10 group-hover:bg-[#211307]/30 transition-colors duration-300" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center gap-2 rounded-full bg-white/95 backdrop-blur text-[#211307] pl-5 pr-4 py-3 text-[14px] font-semibold shadow-lg group-hover:bg-[#EDA13A] transition-colors duration-300">
          <Play size={16} className="fill-current" /> Launch demo
        </span>
      </div>
    </div>
  </a>
);

/* B · Corner pill badge, minimal */
const OptB = () => (
  <a href={URL} target="_blank" rel="noopener noreferrer" className="group block relative rounded-xl overflow-hidden border border-[#3E2818]/12 bg-white shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300">
    <Chrome />
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
      <span className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-[#211307]/90 backdrop-blur text-[#F7F5F0] px-3 py-1.5 text-[11px] font-semibold group-hover:bg-[#EDA13A] group-hover:text-[#211307] transition-colors duration-300">
        Live demo <ArrowUpRight size={13} strokeWidth={2.4} />
      </span>
    </div>
  </a>
);

/* C · Bottom gradient caption (Netflix-thumbnail style) */
const OptC = () => (
  <a href={URL} target="_blank" rel="noopener noreferrer" className="group block relative rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300">
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#211307] via-[#211307]/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#E8AA68]">Finance Close</p>
          <p className="text-[15px] font-semibold text-white mt-0.5">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-white text-[#211307] flex items-center justify-center group-hover:bg-[#EDA13A] transition-colors duration-300 shrink-0"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
      </div>
    </div>
  </a>
);

/* D · Editorial caption card beneath image */
const OptD = () => (
  <a href={URL} target="_blank" rel="noopener noreferrer" className="group block rounded-xl overflow-hidden border border-[#3E2818]/12 bg-white shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300">
    <Chrome />
    <div className="relative aspect-[16/10] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
    </div>
    <div className="p-4">
      <p className="text-[14px] font-semibold text-[#3E2818]">See it in action</p>
      <p className="text-[12px] text-[#3E2818]/55 mt-0.5 leading-snug">A working reconciliation agent — try the interactive demo.</p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#8A5730] group-hover:gap-2.5 group-hover:text-[#EDA13A] transition-all duration-300">
        Open live demo <ArrowUpRight size={15} strokeWidth={2.4} />
      </span>
    </div>
  </a>
);

/* E · Full hover reveal (clean until interaction) */
const OptE = () => (
  <a href={URL} target="_blank" rel="noopener noreferrer" className="group block relative rounded-xl overflow-hidden border border-[#3E2818]/12 bg-white shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] transition-all duration-300">
    <Chrome />
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#211307]/0 group-hover:bg-[#211307]/55 opacity-0 group-hover:opacity-100 transition-all duration-300">
        <span className="flex items-center gap-2 rounded-full bg-[#EDA13A] text-[#211307] px-5 py-2.5 text-[14px] font-semibold translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
          Open live demo <ExternalLink size={15} strokeWidth={2.2} />
        </span>
      </div>
    </div>
  </a>
);

/* F · Floating overlapping button */
const OptF = () => (
  <a href={URL} target="_blank" rel="noopener noreferrer" className="group block relative rounded-xl border border-[#3E2818]/12 bg-white shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300 pb-7">
    <div className="rounded-t-xl overflow-hidden">
      <Chrome />
      <div className="relative aspect-[16/11] overflow-hidden">
        <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
      </div>
    </div>
    <span className="absolute left-1/2 -translate-x-1/2 -bottom-0 translate-y-1/2 flex items-center gap-2 rounded-full bg-[#211307] text-[#F7F5F0] px-5 py-3 text-[13px] font-semibold shadow-[0_12px_28px_-10px_rgba(33,19,7,0.7)] group-hover:bg-[#EDA13A] group-hover:text-[#211307] transition-colors duration-300">
      <MousePointerClick size={15} strokeWidth={2.2} /> Open live demo
    </span>
  </a>
);

const OPTIONS = [
  { id: "a", title: "Option A · Centered Launch Button", note: "Play-style button always centered over the shot — reads instantly as 'click to open', like a video or app-store preview.", comp: OptA },
  { id: "b", title: "Option B · Corner Pill Badge", note: "Clean browser frame with a small 'Live demo ↗' pill pinned to the corner. Minimal and unobtrusive.", comp: OptB },
  { id: "c", title: "Option C · Bottom Gradient Caption", note: "Netflix-thumbnail feel — dark gradient with a title and a round arrow button. Cinematic and clearly interactive.", comp: OptC },
  { id: "d", title: "Option D · Editorial Caption Card", note: "Screenshot with a short caption + inline link beneath. Explains what the demo is, not just that it's clickable.", comp: OptD },
  { id: "e", title: "Option E · Full Hover Reveal", note: "Clean screenshot; a gold CTA sweeps in only on hover. Most minimal — relies on hover to signal clickability.", comp: OptE },
  { id: "f", title: "Option F · Floating Button", note: "A pill button overlaps the bottom edge of the frame — a distinctive, modern 'launch' affordance.", comp: OptF },
];

export default function DemoThumbOptions() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Demo Thumbnail · Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a design for the demo thumbnail</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Each card links to the live Finance reconciliation demo. Hover to feel the interaction. Tell me the letter and I&rsquo;ll apply it in Section&nbsp;4.</p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
